import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Button,
  Fade,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { BackspaceOutlined, ContentCopy, OpenInFull, SaveOutlined, Send } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'

import { AgentModeBadge } from '@/features/events/conversation/agent-mode-badges/AgentModeBadge'
import { AgentModeBadgeTransition } from '@/features/events/conversation/agent-mode-badges/AgentModeBadgeTransition'
import { ZoomControls } from '@/features/events/conversation/controls/components/ZoomControls'
import { AgentMode } from '@/features/events/shared/types'

import { HOME_CONTENT } from '../defaults'
import {
  cardSectionContainerSx,
  cardSectionTitleSx,
  dialogBadgeWrapperSx,
  dialogBreadcrumbSx,
  dialogCloseIconButtonSx,
  dialogControlsRowCenterSx,
  dialogControlsRowLeftSx,
  dialogControlsRowRightSx,
  dialogControlsRowSx,
  dialogPassiveIconButtonSx,
  dialogPointerEventsNoneSx,
  dialogTitleBarSx,
  dialogTitleContentSx,
  dialogTitleRowSx,
  dialogTitleTextSx,
  dialogWindowControlsSx,
  visuallyHiddenSx,
} from '../styles'
import { MobileScreenshotFallback } from './MobileScreenshotFallback'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_HEADING_ID = 'user-input-demo-heading'
const SECTION_DESCRIPTION_ID = 'user-input-demo-description'

const DEMO_TEXT = `Design a real-time notification system for Nexus Web.

Requirements:
- WebSocket gateway with reconnection and exponential backoff
- Priority queue \u2014 critical alerts interrupt, info batches
- Per-user notification preferences stored in PostgreSQL
- Toast stack with max 3 visible, auto-dismiss after 5s
- Browser push for critical alerts when tab is inactive

Start with the WebSocket gateway and the priority queue.
I\u2019ll review the architecture before we touch the UI.`

/** Fixed row count — matches DEMO_TEXT visual lines at 760px container width */
const DEMO_TEXT_ROWS = 12

const TYPING_INTERVAL_MS = 15
const IDLE_PAUSE_MS = 2000
const MODE_SELECT_PAUSE_MS = 1000
const POST_TYPING_PAUSE_MS = 1500
const TRANSITION_DURATION_MS = 1500
const POST_TRANSITION_PAUSE_MS = 3000
const RESET_PAUSE_MS = 2000

const NOOP = () => {}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const UserInputDemoSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <MobileScreenshotFallback
        headingId={SECTION_HEADING_ID}
        title={HOME_CONTENT.userInput.title}
        subtitle={HOME_CONTENT.userInput.description}
        imageSrc={HOME_CONTENT.userInput.image}
        imageAlt={HOME_CONTENT.userInput.imageAlt}
      />
    )
  }

  return <UserInputDesktopDemo />
}

// ---------------------------------------------------------------------------
// Private — Desktop demo (hooks + animated content)
// ---------------------------------------------------------------------------

const UserInputDesktopDemo: FC = () => {
  const theme = useTheme()
  const prefersReducedMotion = useRef(
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const { phase, displayedText } = useDemoTypewriter(prefersReducedMotion.current)
  const hasText = displayedText.length > 0

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={SECTION_HEADING_ID}
      sx={cardSectionContainerSx}
    >
      <Typography id={SECTION_HEADING_ID} component="h2" sx={cardSectionTitleSx}>
        {HOME_CONTENT.userInput.title}
      </Typography>
      <Typography sx={sectionSubtitleSx}>{HOME_CONTENT.userInput.description}</Typography>

      <Typography id={SECTION_DESCRIPTION_ID} sx={visuallyHiddenSx}>
        {HOME_CONTENT.userInput.accessibilitySummary}
      </Typography>

      <Fade in={true} timeout={600}>
        <Box
          role="img"
          aria-label="Demonstration of the user input dialog showing mode switching and a multiline reply being typed"
          aria-describedby={SECTION_DESCRIPTION_ID}
          sx={responsiveWrapperSx}
        >
          <Box sx={chromeContainerSx}>
            <Box sx={dialogTitleBarSx}>
              <Box sx={dialogTitleRowSx}>
                <Box sx={dialogTitleContentSx}>
                  <Box sx={dialogBadgeWrapperSx}>
                    <DemoBadge phase={phase} />
                  </Box>

                  <Typography variant="h6" sx={dialogTitleTextSx}>
                    Reply to Agent
                  </Typography>

                  <Typography variant="body2" sx={dialogBreadcrumbSx}>
                    MacBook Pro &gt; Nexus Daemon &gt; API Gateway
                  </Typography>
                </Box>

                <Box sx={dialogWindowControlsSx}>
                  <IconButton
                    size="small"
                    sx={dialogPassiveIconButtonSx}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <OpenInFull fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={dialogCloseIconButtonSx(theme)}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={controlsRowContainerSx}>
                <Box sx={dialogControlsRowSx}>
                  <Box sx={dialogControlsRowLeftSx}>
                    <Box sx={dialogPointerEventsNoneSx}>
                      <ZoomControls
                        zoomLevel={1}
                        onZoomIn={NOOP}
                        onZoomOut={NOOP}
                        onZoomReset={NOOP}
                      />
                    </Box>
                  </Box>
                  <Box sx={dialogControlsRowCenterSx}>
                    <IconButton
                      size="small"
                      sx={dialogPassiveIconButtonSx}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <ContentCopy sx={centerIconSx} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={hasText ? saveActiveIconSx : disabledIconSx}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <SaveOutlined sx={centerIconSx} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={hasText ? deleteActiveIconSx(theme) : disabledIconSx}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <BackspaceOutlined sx={centerIconSx} />
                    </IconButton>
                  </Box>
                  <Box sx={dialogControlsRowRightSx} />
                </Box>
              </Box>
            </Box>

            <Box sx={contentAreaSx}>
              <Box sx={formContainerSx}>
                <TextField
                  fullWidth
                  multiline
                  rows={DEMO_TEXT_ROWS}
                  value={displayedText}
                  InputProps={{ readOnly: true }}
                  sx={demoTextFieldSx}
                />

                <Typography variant="caption" sx={encryptionTextSx}>
                  All outbound messages are end-to-end encrypted.
                </Typography>

                <Box sx={submitRowSx}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Send />}
                    sx={submitButtonSx}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    Reply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Private — Demo badge renderer
// ---------------------------------------------------------------------------

// Phase sequence: idle (Accept edits) → selected (Plan badge) → typing → sending (transition anim) → done → reset
type DemoPhase = 'idle' | 'selected' | 'typing' | 'sending' | 'done'

interface DemoBadgeProps {
  readonly phase: DemoPhase
}

const DemoBadge: FC<DemoBadgeProps> = ({ phase }) => {
  if (phase === 'sending' || phase === 'done') {
    return (
      <AgentModeBadgeTransition from={AgentMode.AUTO_ACCEPT_EDITS_MODE} to={AgentMode.PLAN_MODE} />
    )
  }

  // idle = Accept edits (user hasn't switched yet); selected/typing = Plan (user switched)
  const mode = phase === 'idle' ? AgentMode.AUTO_ACCEPT_EDITS_MODE : AgentMode.PLAN_MODE

  return (
    <Box sx={dialogPointerEventsNoneSx}>
      <AgentModeBadge agentMode={mode} selectable onChange={NOOP} />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Private — Typewriter hook
// ---------------------------------------------------------------------------

interface UseDemoTypewriterReturn {
  readonly phase: DemoPhase
  readonly displayedText: string
}

function useDemoTypewriter(reducedMotion: boolean): UseDemoTypewriterReturn {
  const [phase, setPhase] = useState<DemoPhase>(reducedMotion ? 'done' : 'idle')
  const [displayedText, setDisplayedText] = useState(reducedMotion ? DEMO_TEXT : '')

  const charIndexRef = useRef(reducedMotion ? DEMO_TEXT.length : 0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Phase sequence: idle → selected → typing → sending → done → idle
  useEffect(() => {
    if (reducedMotion) return

    // idle: Show Accept edits badge, pause, then switch to Plan
    if (phase === 'idle') {
      charIndexRef.current = 0
      setDisplayedText('')
      timeoutRef.current = setTimeout(() => {
        setPhase('selected')
      }, IDLE_PAUSE_MS)
    }

    // selected: Plan badge visible, brief pause before typing starts
    if (phase === 'selected') {
      timeoutRef.current = setTimeout(() => {
        setPhase('typing')
      }, MODE_SELECT_PAUSE_MS)
    }

    // typing: Typewriter effect, then brief pause before "sending"
    if (phase === 'typing') {
      intervalRef.current = setInterval(() => {
        if (charIndexRef.current < DEMO_TEXT.length) {
          charIndexRef.current++
          setDisplayedText(DEMO_TEXT.slice(0, charIndexRef.current))
        } else {
          clearTimers()
          timeoutRef.current = setTimeout(() => {
            setPhase('sending')
          }, POST_TYPING_PAUSE_MS)
        }
      }, TYPING_INTERVAL_MS)
    }

    // sending: Transition animation plays (Accept edits → Plan), then hold
    if (phase === 'sending') {
      timeoutRef.current = setTimeout(() => {
        setPhase('done')
      }, TRANSITION_DURATION_MS)
    }

    // done: Show final state, then reset
    if (phase === 'done') {
      timeoutRef.current = setTimeout(() => {
        setPhase('idle')
      }, POST_TRANSITION_PAUSE_MS + RESET_PAUSE_MS)
    }

    return clearTimers
  }, [phase, reducedMotion, clearTimers])

  return { phase, displayedText }
}

const sectionSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: 2,
}

const responsiveWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}

const chromeContainerSx: SxProps<Theme> = (theme) => ({
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  width: '100%',
  maxWidth: 760,
})

const controlsRowContainerSx: SxProps<Theme> = {
  pt: 3,
}

const centerIconSx: SxProps<Theme> = {
  fontSize: '1.1rem',
}

const disabledIconSx: SxProps<Theme> = {
  color: 'action.disabled',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const saveActiveIconSx: SxProps<Theme> = {
  color: 'primary.main',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const deleteActiveIconSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.error.main,
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
})

// ---------------------------------------------------------------------------
// Styles - Content area
// ---------------------------------------------------------------------------

const contentAreaSx: SxProps<Theme> = {
  pt: 5,
  px: 3,
  pb: 3,
}

const formContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const demoTextFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-input': {
    cursor: 'default',
  },
  '& .MuiOutlinedInput-root': {
    cursor: 'default',
  },
}

// ---------------------------------------------------------------------------
// Styles - Submit button
// ---------------------------------------------------------------------------

const encryptionTextSx: SxProps<Theme> = {
  color: 'success.main',
}

const submitRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
}

const submitButtonSx: SxProps<Theme> = {
  cursor: 'default',
  textTransform: 'none',
  '&:hover': { backgroundColor: 'primary.main' },
}
